import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import Reveal from "@/components/Reveal";
import ProjectModal from "@/components/ProjectModal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const imgSrc = (p) =>
  p && p.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p;

const Hook = () => (
  <svg width="16" height="40" viewBox="0 0 16 40" className="-mt-2 block" aria-hidden="true">
    <path
      d="M8 40 V14 C8 7 9.5 2.5 12.5 2.5 C15.5 2.5 15.8 7 13.6 9.5"
      fill="none"
      stroke="#5c5c5c"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  </svg>
);

const Rail = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then(({ data }) => {
        if (!Array.isArray(data)) return;
        const withRender = data
          .filter((d) => d.render)
          .map((d) => ({ ...d, img: imgSrc(d.render) }));
        const source = withRender.length
          ? withRender
          : data.map((d) => ({ ...d, img: imgSrc(d.image) }));
        setItems(source);
      })
      .catch(() => {
        setItems(t.rail.items.map((it) => ({ ...it, img: "/images/gen-kaftan.png" })));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      id="rail"
      data-testid="rail-section"
      className="overflow-hidden border-b border-ink/10 pb-20 pt-24 md:pt-28"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-clay">{t.rail.overline}</p>
            <h2 data-testid="rail-heading" className="mt-6 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
              {t.rail.h2a} <em className="italic text-clay">{t.rail.h2b}</em>
            </h2>
          </div>
          <p className="max-w-sm text-base leading-relaxed text-ink/60">{t.rail.sub}</p>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div data-testid="rail-track" className="relative mt-14">
          <div
            className="absolute inset-x-0 top-0 h-1.5 rounded-full bg-gradient-to-b from-neutral-300 via-neutral-500 to-neutral-700"
            aria-hidden="true"
          />
          <div
            className="absolute inset-x-10 top-2 h-8 rounded-full bg-ink/15 blur-xl"
            aria-hidden="true"
          />
          <div
            className="flex flex-nowrap items-start justify-center px-2 pt-0"
            onMouseLeave={() => setActive(null)}
          >
            {items.map((it, gi) => {
              const isActive = active === gi;
              return (
                <div
                  key={it.id || it.name}
                  className={`swing pointer-events-none relative flex flex-col items-center ${gi > 0 ? "-ms-8" : ""}`}
                  style={{
                    animationDuration: `${4.6 + (gi % 5) * 0.5}s`,
                    animationDelay: `${gi * 0.35}s`,
                    zIndex: isActive ? 40 : gi,
                  }}
                >
                  <Hook />
                  <div style={{ perspective: "900px" }} className="relative -mt-px">
                    <motion.button
                      onMouseEnter={() => setActive(gi)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActive(gi);
                        setSelected(it);
                      }}
                      animate={{
                        rotateY: isActive ? 0 : 68,
                        scale: isActive ? 1.5 : 1,
                        opacity: active === null || isActive ? 1 : 0.5,
                      }}
                      transition={{ type: "spring", stiffness: 300, damping: 24 }}
                      style={{ transformStyle: "preserve-3d", transformOrigin: "top center" }}
                      data-testid={`rail-item-${gi}`}
                      aria-label={it.name}
                      className="pointer-events-auto relative block w-14 cursor-pointer bg-transparent p-0 sm:w-20 md:w-24 lg:w-28 xl:w-32"
                    >
                      <img
                        src={it.img}
                        alt={it.name}
                        loading="lazy"
                        draggable="false"
                        className="h-auto w-full object-contain drop-shadow-[0_16px_14px_rgba(0,0,0,0.20)]"
                      />
                    </motion.button>
                  </div>
                  <AnimatePresence>
                    {isActive && (
                      <motion.p
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18 }}
                        data-testid={`rail-caption-${gi}`}
                        className="absolute top-full z-40 mt-3 w-40 whitespace-normal rounded-full border border-ink/10 bg-bone/95 px-4 py-2 text-center font-serif text-sm leading-tight tracking-tight text-ink shadow-[0_12px_30px_rgba(0,0,0,0.12)] backdrop-blur-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {it.name}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>

      <ProjectModal project={selected} img={selected?.img} onClose={() => setSelected(null)} />
    </section>
  );
};

export default Rail;
